import {
  maintenanceLogSteps,
  maintenanceLogs,
  propertyTypeEnum,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'

// Auto-generate base schemas
export const selectMaintenanceLogSchema = createSelectSchema(maintenanceLogs)
export const insertMaintenanceLogSchema = createInsertSchema(
  maintenanceLogs,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  completedByUserId: true, // Auto-populated by backend via contextFields
})

export const selectMaintenanceLogStepSchema =
  createSelectSchema(maintenanceLogSteps)
export const insertMaintenanceLogStepSchema = createInsertSchema(
  maintenanceLogSteps,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// Maintenance log with relations
export const selectMaintenanceLogWithRelationsSchema = withAttachments(
  selectMaintenanceLogSchema.extend({
    property: z
      .object({
        id: z.number(),
        customerId: z.number(),
        propertyType: z.enum(propertyTypeEnum.enumValues),
        constructionYear: z.number().nullable(),
        notes: z.string().nullable(),
      })
      .optional(),
    visit: z
      .object({
        id: z.number(),
        subscriptionId: z.number(),
        scheduledDate: z.string().nullable().optional(),
        status: z.enum(['scheduled', 'in_progress', 'completed', 'cancelled']),
      })
      .nullable()
      .optional(),
    maintenanceTask: z
      .object({
        id: z.number(),
        name: z.string(),
        description: z.string().nullable(),
        category: z.string().nullable(),
        estimatedCost: z.string().nullable(),
      })
      // Maintenance task could have been deleted (in this case null)
      .nullable()
      .optional(),
    completedByUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable()
      .optional(),
    steps: z
      .array(
        selectMaintenanceLogStepSchema.extend({
          maintenanceTaskStep: z
            .object({
              id: z.number(),
              maintenanceTaskId: z.number(),
              stepNumber: z.number(),
              name: z.string(),
              description: z.string().nullable(),
            })
            .optional(),
        }),
      )
      .optional(),
  }),
)

// Alias for backwards compatibility
export const selectMaintenanceLogWithDetailsSchema =
  selectMaintenanceLogWithRelationsSchema

// Create maintenance log with steps
export const createMaintenanceLogWithStepsSchema =
  insertMaintenanceLogSchema.extend({
    steps: z
      .array(
        insertMaintenanceLogStepSchema.omit({
          maintenanceLogId: true,
        }),
      )
      .optional(),
  })

// Update schemas
export const updateMaintenanceLogSchema = insertMaintenanceLogSchema.partial()
export const updateMaintenanceLogStepSchema =
  insertMaintenanceLogStepSchema.partial()

// Type exports
export type MaintenanceLog = z.infer<typeof selectMaintenanceLogSchema>
export type MaintenanceLogWithRelations = z.infer<
  typeof selectMaintenanceLogWithRelationsSchema
>
export type MaintenanceLogStep = z.infer<typeof selectMaintenanceLogStepSchema>
export type CreateMaintenanceLog = z.infer<typeof insertMaintenanceLogSchema>
export type CreateMaintenanceLogWithSteps = z.infer<
  typeof createMaintenanceLogWithStepsSchema
>
export type UpdateMaintenanceLog = z.infer<typeof updateMaintenanceLogSchema>
export type CreateMaintenanceLogStep = z.infer<
  typeof insertMaintenanceLogStepSchema
>
export type UpdateMaintenanceLogStep = z.infer<
  typeof updateMaintenanceLogStepSchema
>
